import { useState } from 'react'
import { type User, useAvailableRoles } from '~/api/users'
import styles from './UserModal.module.css'

interface UserModalProps {
  user?: User | null
  isOpen: boolean
  onClose: () => void
  onSave: (data: UserFormData) => Promise<void>
  isSaving: boolean
}

export interface UserFormData {
  username: string
  email: string
  firstName: string
  lastName: string
  password: string
  temporaryPassword: boolean
  enabled: boolean
  roles: string[]
}

const INITIAL_FORM: UserFormData = {
  username: '',
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  temporaryPassword: false,
  enabled: true,
  roles: ['customer'],
}

function formFromUser(user?: User | null): UserFormData {
  if (!user) return INITIAL_FORM

  return {
    username: user.username,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    password: '',
    temporaryPassword: false,
    enabled: user.enabled,
    roles: user.roles,
  }
}

export function UserModal(props: UserModalProps) {
  if (!props.isOpen) return null

  return <UserModalContent key={props.user?.id ?? 'new-user'} {...props} />
}

function UserModalContent({ user, onClose, onSave, isSaving }: UserModalProps) {
  const [form, setForm] = useState<UserFormData>(() => formFromUser(user))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const { data: availableRoles } = useAvailableRoles()

  const isEditing = !!user

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    if (!isEditing && !form.username.trim()) newErrors.username = 'Username is required'
    if (!form.email.trim()) newErrors.email = 'Email is required'
    if (!isEditing && !form.password) newErrors.password = 'Password is required'
    if (!isEditing && form.password.length > 0 && form.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    }
    if (form.roles.length === 0) newErrors.roles = 'At least one role is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    await onSave(form)
  }

  const toggleRole = (role: string) => {
    setForm(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role],
    }))
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>{isEditing ? 'EDIT USER' : 'CREATE USER'}</h2>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Username</label>
              <input
                type="text"
                className={styles.input}
                value={form.username}
                onChange={e => setForm(prev => ({ ...prev, username: e.target.value }))}
                disabled={isEditing}
                placeholder="username"
              />
              {errors.username && <span className={styles.error}>{errors.username}</span>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Email</label>
              <input
                type="email"
                className={styles.input}
                value={form.email}
                onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@example.com"
              />
              {errors.email && <span className={styles.error}>{errors.email}</span>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>First Name</label>
              <input
                type="text"
                className={styles.input}
                value={form.firstName}
                onChange={e => setForm(prev => ({ ...prev, firstName: e.target.value }))}
                placeholder="First name"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Last Name</label>
              <input
                type="text"
                className={styles.input}
                value={form.lastName}
                onChange={e => setForm(prev => ({ ...prev, lastName: e.target.value }))}
                placeholder="Last name"
              />
            </div>

            {!isEditing && (
              <>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Password</label>
                  <input
                    type="password"
                    className={styles.input}
                    value={form.password}
                    onChange={e => setForm(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="••••••••"
                  />
                  {errors.password && <span className={styles.error}>{errors.password}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={form.temporaryPassword}
                      onChange={e => setForm(prev => ({ ...prev, temporaryPassword: e.target.checked }))}
                    />
                    <span>Require password change on first login</span>
                  </label>
                </div>
              </>
            )}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Roles</label>
            <div className={styles.rolesGrid}>
              {(availableRoles ?? ['admin', 'customer', 'driver', 'operations_manager']).map(role => (
                <label key={role} className={styles.roleCheckbox}>
                  <input
                    type="checkbox"
                    checked={form.roles.includes(role)}
                    onChange={() => toggleRole(role)}
                  />
                  <span>{role.replace(/_/g, ' ')}</span>
                </label>
              ))}
            </div>
            {errors.roles && <span className={styles.error}>{errors.roles}</span>}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={e => setForm(prev => ({ ...prev, enabled: e.target.checked }))}
              />
              <span>Account enabled</span>
            </label>
          </div>

          <div className={styles.actions}>
            <button type="button" className={styles.cancelButton} onClick={onClose}>
              CANCEL
            </button>
            <button type="submit" className={styles.saveButton} disabled={isSaving}>
              {isSaving ? 'SAVING...' : isEditing ? 'UPDATE' : 'CREATE'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
